import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { Checkbox } from "./ui/checkbox";
import { Shield, FileText, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { TERMS_SECTIONS, PRIVACY_SECTIONS, TOS_VERSION, PRIVACY_VERSION } from "../lib/legal";
import { toast } from "sonner";

export default function ConsentModal({ open, onAccepted }) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [shakeCheckbox, setShakeCheckbox] = useState(false);

  useEffect(() => {
    if (!open) { setChecked(false); setError(""); }
  }, [open]);

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      await api.post("/legal/consent", {
        tos_version: TOS_VERSION,
        privacy_version: PRIVACY_VERSION,
      });
      try { localStorage.setItem("linguasub_consent_v", TOS_VERSION); } catch { /* ignore */ }
      toast.success("Thanks — you're set.");
      onAccepted?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not save consent. Please try again.";
      setError(typeof msg === "string" ? msg : "Could not save consent. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = async () => {
    if (submitting) return;
    if (!checked) {
      setError("Please tick the box to confirm you agree.");
      setShakeCheckbox(true);
      setTimeout(() => setShakeCheckbox(false), 500);
      return;
    }
    await submit();
  };

  return (
    <Dialog open={open}>
      <DialogContent
        data-testid="consent-modal"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        className="bg-zinc-950 border border-zinc-800 rounded-none max-w-2xl w-[92vw] p-0 text-white !grid-rows-[auto_1fr_auto] max-h-[92dvh] overflow-hidden [&>button:last-child]:hidden"
      >
        <DialogHeader className="p-6 md:p-8 border-b border-zinc-800 text-left flex-shrink-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-2 flex items-center gap-1.5">
            <Shield className="w-3 h-3" /> One-time consent
          </div>
          <DialogTitle className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            Before you get started
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-400 mt-2">
            Please read our Terms and Privacy Policy. You must own, or have permission for,
            anything you upload. LinguaSub is a tool — you remain responsible for the rights
            to the content you process.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="min-h-0 overflow-y-auto">
          <div className="p-6 md:p-8 space-y-8">
            <section>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-1.5">
                <FileText className="w-3 h-3" /> Terms of Service · v{TOS_VERSION}
              </div>
              <div className="space-y-4">
                {TERMS_SECTIONS.map((s) => (
                  <div key={s.title}>
                    <h4 className="text-sm font-semibold text-white mb-1">{s.title}</h4>
                    <p className="text-xs leading-relaxed text-zinc-400">{s.body}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-1.5">
                <Shield className="w-3 h-3" /> Privacy Policy · v{PRIVACY_VERSION}
              </div>
              <div className="space-y-4">
                {PRIVACY_SECTIONS.map((s) => (
                  <div key={s.title}>
                    <h4 className="text-sm font-semibold text-white mb-1">{s.title}</h4>
                    <p className="text-xs leading-relaxed text-zinc-400">{s.body}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </ScrollArea>

        <div className="p-5 md:p-8 border-t border-zinc-800 space-y-3 flex-shrink-0 bg-zinc-950">
          <div
            role="button"
            tabIndex={0}
            data-testid="consent-checkbox-label"
            onClick={() => { setChecked((v) => !v); setError(""); }}
            onKeyDown={(e) => {
              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                setChecked((v) => !v);
                setError("");
              }
            }}
            className={`flex items-start gap-3 cursor-pointer select-none border p-3 transition-all duration-200 ${shakeCheckbox ? "border-red-500 bg-red-500/5 animate-pulse" : "border-transparent hover:bg-zinc-900/60"}`}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={(v) => { setChecked(!!v); setError(""); }}
              onClick={(e) => e.stopPropagation()}
              data-testid="consent-checkbox"
              className="mt-0.5 border-zinc-700 data-[state=checked]:bg-white data-[state=checked]:text-zinc-950 pointer-events-auto"
            />
            <span className="text-sm text-zinc-300 leading-relaxed">
              I have read and agree to the Terms and Privacy Policy. I confirm I own or have
              permission for any audio, video, or text I upload.
            </span>
          </div>

          {error && (
            <div
              data-testid="consent-error"
              className="flex items-start gap-2 text-xs font-mono text-red-400 border border-red-500/30 bg-red-500/5 px-3 py-2"
            >
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <Button
            onClick={handleAccept}
            disabled={submitting}
            data-testid="consent-accept-btn"
            className={`w-full h-11 font-semibold rounded-md transition-colors duration-200 ${checked ? "bg-white text-zinc-950 hover:bg-zinc-200" : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"}`}
          >
            {submitting ? "Saving..." : "Accept and continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
